import React, { CSSProperties } from "react"
import { FormPDF, FormPDFAssetHeader } from "./components/FormPDF"
import { PDFHeaderBackground, PDFMinEntryHeight, PDFSectionBorder } from "./components/PDF"
import { PDFSection } from "./components/PDFSection"
import { PDFAsset } from "./models/PDFAsset"
import { PDFAssetPart } from "./models/PDFAssetPart"

/**
 * Action Item 6764: form-style Parts List PDF. Same parts table as the legacy
 * PartsListPDF; only the top of the page is cleaned up (page header line at the very
 * top, compact asset header) with the freed space applied after the asset information.
 */
export type PartsListFormPDFProps = {
    asset: PDFAsset
    companyName: string
    parts: PDFAssetPart[]
    companyLogoUrl: string | null
}

export function PartsListFormPDF({ asset, companyName, parts, companyLogoUrl }: PartsListFormPDFProps): JSX.Element {
    return (
        <FormPDF pageHeaderTitle={`Parts List - ${asset.label}`} companyName={companyName}>
            <PartsListFormPage asset={asset} companyName={companyName} parts={parts} companyLogoUrl={companyLogoUrl} />
        </FormPDF>
    )
}

export function PartsListFormPage({ asset, parts, companyLogoUrl }: PartsListFormPDFProps): JSX.Element {
    return (
        <>
            <FormPDFAssetHeader
                title={`Parts List`}
                asset={asset}
                showSerialNumber={true}
                showLastKnownLocation={false}
                companyLogoUrl={companyLogoUrl}
            />
            <PDFSection style={{ breakInside: "initial", marginTop: 16 }}>
                <table
                    style={{
                        borderCollapse: "collapse",
                        tableLayout: "auto",
                        width: "100%",
                        border: PDFSectionBorder,
                    }}
                >
                    <thead>
                        <tr>
                            <PartHead style={{ textAlign: "center" }}>Qty.</PartHead>
                            <PartHead>Unit</PartHead>
                            <PartHead style={{ textAlign: "center" }}>Part Number</PartHead>
                            <PartHead style={{ maxWidth: "20vw" }}>Description</PartHead>
                            <PartHead>Vendor</PartHead>
                            <PartHead style={{ maxWidth: "30vw" }}>Note</PartHead>
                        </tr>
                    </thead>
                    <tbody>
                        {parts.map((it, i) => (
                            <Part key={i} part={it} />
                        ))}
                    </tbody>
                </table>
            </PDFSection>
        </>
    )
}

function Part({ part }: { part: PDFAssetPart }): JSX.Element {
    return (
        <tr style={{ breakInside: "avoid", border: PDFSectionBorder }}>
            <PartCell style={{ textAlign: "center" }}>{part.quantity}</PartCell>
            <PartCell>{part.unit}</PartCell>
            <PartCell style={{ textAlign: "center" }}>{part.partNumber}</PartCell>
            <PartCell style={{ maxWidth: "20vw" }}>{part.description}</PartCell>
            <PartCell>{part.vendor}</PartCell>
            <PartCell style={{ maxWidth: "30vw" }}>{part.note}</PartCell>
        </tr>
    )
}

function PartHead({ children, style }: { children?: React.ReactNode; style?: CSSProperties }): JSX.Element {
    return (
        <th
            className="pdf-table-label"
            style={{
                fontWeight: 700,
                background: PDFHeaderBackground,
                border: PDFSectionBorder,
                padding: "2px 8px",
                fontSize: "1.4rem",
                minHeight: PDFMinEntryHeight,
                textAlign: "left",
                ...style,
            }}
        >
            {children}
        </th>
    )
}

function PartCell({ children, style }: { children?: React.ReactNode; style?: CSSProperties }): JSX.Element {
    return (
        <td
            className="pdf-table-data"
            style={{
                padding: "2px 8px",
                fontSize: "1.4rem",
                minHeight: PDFMinEntryHeight,
                border: PDFSectionBorder,
                ...style,
            }}
        >
            {children}
        </td>
    )
}
